import { useQueries, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAppConfig, useRegionConfig } from "providers";
import { useFeatureManager } from "hooks/useFeatureManager";
import { ReferencesController, type ReferenceValue } from "../controllers/referencesController";

const ONE_WEEK_IN_MILLISECONDS = 7 * 24 * 60 * 60 * 1000;

export const REF_TYPES = {
    PRESENTING_ISSUES: 'issues',
    MODALITIES: 'modalities',
    SERVICES: 'services',
    INDUSTRIES: 'industries',
} as const;

// Initialize controller with API host
const useController = () => {
    const { REACT_APP_GRAPHQL_API_HOST } = useAppConfig();
    return new ReferencesController(REACT_APP_GRAPHQL_API_HOST);
};

// i18n.language can resolve to a bare language (e.g. "en") when it falls back,
// so map it back to the region-specific locale code (e.g. "en-CA") the API expects.
const useCurrentReferencesLocale = () => {
    const { i18n } = useTranslation();
    const { languages } = useRegionConfig();
    const currentLanguage = languages.find(language => language.code === i18n.language || language.fallback.includes(i18n.language));
    return currentLanguage?.code || i18n.language;
};

const useIsReferencesFeatureEnabled = () => {
    const { isIncidentReportFeatureEnabled, isCriticalIncidentFeatureEnabled } = useFeatureManager();
    return isIncidentReportFeatureEnabled() || isCriticalIncidentFeatureEnabled();
};

export const useGetReferences = (referenceFieldName: typeof REF_TYPES[keyof typeof REF_TYPES]) => {
    const controller = useController();
    const locale = useCurrentReferencesLocale();
    const isFeatureEnabled = useIsReferencesFeatureEnabled();

    return useQuery<ReferenceValue[]>({
        queryKey: ['references', referenceFieldName, locale],
        queryFn: () => controller.getReferences(referenceFieldName, locale),
        enabled: !!referenceFieldName && !!locale && isFeatureEnabled,
        staleTime: ONE_WEEK_IN_MILLISECONDS,
        gcTime: ONE_WEEK_IN_MILLISECONDS,
    });
};

export type AllReferences = Record<typeof REF_TYPES[keyof typeof REF_TYPES], ReferenceValue[] | undefined>;

export const useGetAllReferences = () => {
    const controller = useController();
    const locale = useCurrentReferencesLocale();
    const isFeatureEnabled = useIsReferencesFeatureEnabled();
    const referenceFieldNames = Object.values(REF_TYPES);

    const results = useQueries({
        queries: referenceFieldNames.map(referenceFieldName => ({
            queryKey: ['references', referenceFieldName, locale],
            queryFn: () => controller.getReferences(referenceFieldName, locale),
            enabled: !!locale && isFeatureEnabled,
            staleTime: ONE_WEEK_IN_MILLISECONDS,
            gcTime: ONE_WEEK_IN_MILLISECONDS,
        })),
    });

    const data = referenceFieldNames.reduce((acc, referenceFieldName, index) => {
        acc[referenceFieldName] = results[index].data;
        return acc;
    }, {} as AllReferences);

    return {
        data,
        isLoading: results.some(result => result.isLoading),
        isError: results.some(result => result.isError),
    };
};
